import { Card } from 'components/Card';
import {
  ModalScreens,
  ScreenProps,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  selectAseTplFieldInstanceCheck,
  selectAseResultFieldInstanceCheck,
  selectAseIsEditable,
  selectAseIsOnline,
  selectAseTplFieldInstanceDefaultChecksForGroup,
  selectAseFieldInstanceAseMerged,
} from 'store/assessments/selectors';
import {
  AseFieldInstanceCheckStatusEnum,
  AseFieldInstanceStatuses,
  AseResultInstanceCheck,
  FieldInstanceCheckScheduleStatusEnum,
} from 'store/assessments/types';
import { CardRow } from 'components/CardRow';
import { NAVIGATE_MODAL } from 'store/actions';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { ICONS } from 'components/Icon';
import { formatDate, numberToDate } from 'lib/riskbase';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { startCase } from 'lodash';
import { theme } from 'lib/theme';
import { PageHeader } from 'components/PageHeader';

export const AseInstanceCheckDisplay: React.FC<
  ScreenProps<Stacks.ASEREPEATABLE, Screens.AseInstanceCheck>
> = ({ navigation, route, isModal }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const { id, instanceId } = route.params;

  const isEditable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);
  const isQc = isEditable && isOnline;

  const check = useSelector((state) =>
    selectAseTplFieldInstanceCheck(state, id),
  );
  const selectedData = useSelector((state) =>
    selectAseResultFieldInstanceCheck(state, id, instanceId),
  ) || {
    checkId: id,
    instanceId,
    remarks: '',
    statusId: AseFieldInstanceCheckStatusEnum.completed,
  };
  const checkResult: AseResultInstanceCheck = selectedData;

  const instance = useSelector((state) =>
    selectAseFieldInstanceAseMerged(state, instanceId),
  );

  const actualCheckData = instance?.checks?.[id];
  const defaultCheckData = useSelector((state) =>
    selectAseTplFieldInstanceDefaultChecksForGroup(
      state,
      instance?.groupId || '-1',
    ),
  )[id];
  const checkData = actualCheckData || defaultCheckData;

  const handleEditPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseInstanceCheck,
        params: {
          id: selectedData.checkId,
          instanceId: selectedData.instanceId,
        },
      }),
    );

  if (!check) {
    return null;
  }

  const lastCompleted = checkData?.lastCompleted;
  const lastAttempted = checkData?.lastAttempted;
  const due = checkData?.schedule.due;

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={check.name}
          onPressBackButton={
            !isModal && screenSize === 'small' ? navigation?.goBack : undefined
          }
          onPressSaveButton={isQc ? handleEditPress : undefined}
          saveButtonText="Edit"
        />
      }
    >
      {isEditable && (
        <>
          <Card label="Overview">
            <CardRow
              labelAlignTop
              label={`Due ${startCase(check.type.period)}`}
              value={
                due
                  ? `Current Period: ${formatDate(
                      numberToDate(due.from),
                      UI_DATE_FORMAT_LONG,
                    )} – ${formatDate(
                      numberToDate(due.to),
                      UI_DATE_FORMAT_LONG,
                    )}`
                  : undefined
              }
            />
            {checkData?.schedule.statusId ===
            FieldInstanceCheckScheduleStatusEnum.completed ? (
              <CardRow
                labelAlignTop
                label="Check Not Required"
                value="A check has already been completed in this period"
                navigationIcon={ICONS.OctagonCheckSolid}
              />
            ) : (
              <CardRow
                labelAlignTop
                label="Check Required"
                value="A check has not been completed in this period"
                // navigationIcon={ICONS.OctagonSolid}
              />
            )}
          </Card>
          <Card>
            <CardRow
              label="Last Completed"
              value={
                lastCompleted
                  ? formatDate(
                      numberToDate(lastCompleted.date),
                      UI_DATE_FORMAT_LONG,
                    )
                  : 'Never'
              }
              valueColor={
                !lastCompleted || (due && lastCompleted.date < due.from)
                  ? theme.colors.red
                  : theme.colors.green
              }
            />
            {lastAttempted &&
              (!lastCompleted || lastCompleted.date < lastAttempted.date) && (
                <CardRow
                  label="Last Attempted"
                  value={formatDate(
                    numberToDate(lastAttempted.date),
                    UI_DATE_FORMAT_LONG,
                  )}
                />
              )}
          </Card>

          <SeparatorHorizontal />
        </>
      )}

      <Card label={isEditable ? 'In this assessment...' : ''}>
        <CardRow
          label={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.completed]
              .label
          }
          navigationIcon={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.completed]
              .icon
          }
          navigationIconColor={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.completed]
              .color
          }
          radioValue={
            checkResult.statusId === AseFieldInstanceCheckStatusEnum.completed
          }
          disabled={
            checkResult.statusId !== AseFieldInstanceCheckStatusEnum.completed
          }
          labelAlignTop
        />
        <CardRow
          label={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.attempted]
              .label
          }
          navigationIcon={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.attempted]
              .icon
          }
          navigationIconColor={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.attempted]
              .color
          }
          radioValue={
            checkResult.statusId === AseFieldInstanceCheckStatusEnum.attempted
          }
          disabled={
            checkResult.statusId !== AseFieldInstanceCheckStatusEnum.attempted
          }
          labelAlignTop
        />
        <CardRow
          label={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.skipped]
              .label
          }
          navigationIcon={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.skipped]
              .icon
          }
          navigationIconColor={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.skipped]
              .color
          }
          radioValue={
            checkResult.statusId === AseFieldInstanceCheckStatusEnum.skipped
          }
          disabled={
            checkResult.statusId !== AseFieldInstanceCheckStatusEnum.skipped
          }
          labelAlignTop
        />
      </Card>

      {!!checkResult.remarks && (
        <Card label="Remarks">
          <CardRow
            placeholder="No Remarks"
            value={checkResult.remarks}
            labelAlignTop
          />
        </Card>
      )}

      {/* <Card label="Last Check Dates">
        <CardRow
          label="Completed"
          value="25 May 2023"
          navigationIcon={ICONS.OctagonCheckSolid}
        />
        <CardRow
          label="Attempted"
          value="29 May 2023"
          navigationIcon={ICONS.OctagonXmarkSolid}
        />
      </Card> */}
    </Page>
  );
};
